import { createMemoryHistory, createRouter } from "vue-router";
import { defu } from "defu";
import { reactiveComputed } from "@vueuse/core";
import { ParallelRouteNotFoundSymbol } from "./symbols.js";
import { extractParallelRoutePath } from "./utils.js";
import { defineNuxtPlugin, useRouter } from "#app";
import pagesPlusOptions from "#build/nuxt-pages-plus-options.mjs";
import { reactive } from "#imports";
const DEBUG = import.meta.dev && import.meta.client && import.meta.env.VITE_PAGES_PLUS_DEBUG;
export default defineNuxtPlugin(async () => {
  const router = useRouter();
  const { separator, parallelPages: pagesOptions } = pagesPlusOptions;
  if (DEBUG)
    console.log("global router (before)", router.getRoutes());
  const parallelRoutes = router.getRoutes().reduce((acc, route) => {
    const parallelRoutePath = extractParallelRoutePath(route.path, separator);
    if (parallelRoutePath) {
      ;
      (acc[parallelRoutePath.name] ??= []).push({
        ...route,
        path: parallelRoutePath.path
      });
      if (route.name && router.hasRoute(route.name))
        router.removeRoute(route.name);
    }
    return acc;
  }, {});
  if (DEBUG)
    console.log("parallelRoutes", parallelRoutes);
  const parallelRouters = {};
  const _parallelRoutes = {};
  for (const [group, routes] of Object.entries(parallelRoutes)) {
    const parallelRouter = await createParallelRouter(group, routes, router, pagesOptions[group] ?? {});
    parallelRouters[group] = parallelRouter;
    _parallelRoutes[group] = reactiveComputed(() => parallelRouter.currentRoute.value);
    if (DEBUG)
      console.log(`parallelRouter[${group}]`, parallelRouter.getRoutes());
  }
  if (DEBUG)
    console.log("global router (after)", router.getRoutes());
  return {
    provide: {
      parallelRouters,
      parallelRoutes: _parallelRoutes
    }
  };
});
async function createParallelRouter(name, routes, router, parallelPageOptions) {
  const options = defu(parallelPageOptions, {
    mode: "sync",
    index: "/~index",
    fallback: true
  });
  const parallelRouter = createRouter({
    history: createMemoryHistory(),
    routes
  });
  parallelRouter.addRoute({
    path: "/:all(.*)*",
    name: ParallelRouteNotFoundSymbol,
    component: { render: () => void 0 }
  });
  function hasPath(path) {
    return parallelRouter.resolve(path)?.name !== ParallelRouteNotFoundSymbol;
  }
  const fallback = reactive({
    notFound: false,
    index: false
  });
  function tryPush(path, fallbackRedirect = typeof options.fallback === "object" && options.fallback.redirect) {
    function pushWithFallback(path2, ...fallbacks) {
      if (options.fallback === false || hasPath(path2))
        return parallelRouter.push(path2);
      for (const _path of fallbacks) {
        if (_path !== void 0)
          return parallelRouter.push(_path);
      }
    }
    const push = pushWithFallback(path, fallbackRedirect || void 0);
    if (push) {
      return push.then(() => {
        Object.assign(fallback, {
          notFound: false,
          index: false
        });
      });
    } else {
      fallback.notFound = true;
    }
  }
  function sync() {
    return tryPush(router.currentRoute.value.path);
  }
  function setSync(sync2) {
    options.mode = sync2 ? "sync" : "manual";
  }
  async function init() {
    async function tryIndex() {
      const pushIndex = options.index && tryPush(options.index);
      if (pushIndex) {
        await pushIndex;
      } else {
        fallback.index = true;
      }
    }
    if (options.mode === "manual") {
      await tryIndex();
    } else {
      const initSync = sync();
      if (initSync) {
        await initSync;
      } else {
        await tryIndex();
      }
    }
  }
  await init();
  router.beforeResolve(async (to) => {
    if (options.mode === "sync")
      await tryPush(to.path);
  });
  return {
    ...parallelRouter,
    name,
    fallback,
    hasPath,
    tryPush,
    sync,
    setSync
  };
}
